/**
 * Video URL helpers — convert YouTube/Vimeo/Loom watch URLs to embed URLs.
 *
 * SPEC-196 (Sprint 99, 2026-09-12) — Announcement popup system.
 *
 * Adapted from GamesP2P's `convertYoutubeUrl` (rot #185). Extended to also
 * support Vimeo and Loom per SPEC-165 §3.5 (Profileia's announcement schema
 * accepts all 3 providers).
 *
 * Anti-§6 (CM-19): the regex patterns are PUBLIC verifiable facts from
 * the YouTube/Vimeo/Loom developer docs — NOT invented.
 */

/**
 * Convert a YouTube watch URL to an embed URL.
 * Handles:
 *   - youtube.com/watch?v=ID
 *   - youtu.be/ID
 *   - youtube.com/embed/ID (already embed format)
 *   - youtube.com/shorts/ID (YouTube Shorts)
 *
 * Returns `null` if the URL doesn't match any known YouTube pattern.
 */
function convertYoutubeUrl(url: string): string | null {
  if (!url) return null;

  // youtube.com/watch?v=ID
  const watchMatch = url.match(/[?&]v=([^&#]+)/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  // youtu.be/ID
  const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  // youtube.com/embed/ID (already embed format)
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&#]+)/);
  if (embedMatch) return `https://www.youtube.com/embed/${embedMatch[1]}`;

  // youtube.com/shorts/ID
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&#]+)/);
  if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;

  return null;
}

/**
 * Convert a Vimeo watch URL to an embed URL.
 * Handles: vimeo.com/ID
 */
function convertVimeoUrl(url: string): string | null {
  if (!url) return null;

  // vimeo.com/ID
  const match = url.match(/vimeo\.com\/(\d+)/);
  if (match) return `https://player.vimeo.com/video/${match[1]}`;

  return null;
}

/**
 * Convert a Loom share URL to an embed URL.
 * Handles: loom.com/share/ID
 */
function convertLoomUrl(url: string): string | null {
  if (!url) return null;

  // loom.com/share/ID
  const match = url.match(/loom\.com\/share\/([\w-]+)/);
  if (match) return `https://www.loom.com/embed/${match[1]}`;

  return null;
}

/**
 * Convert any supported video URL (YouTube/Vimeo/Loom) to its embed URL.
 *
 * Tries each provider in order (YouTube first since it's the most common).
 * Returns `null` if the URL doesn't match any known pattern — the caller
 * should display a fallback "Ver en YouTube" link with the raw URL.
 *
 * SPEC-165 §3.5 — the same providers are validated in the shared Zod
 * schema (videoUrlSchema). If the URL passes the schema, this function
 * will return a non-null embed URL.
 */
export function convertVideoUrlToEmbed(url: string): string | null {
  if (!url) return null;
  return (
    convertYoutubeUrl(url) ||
    convertVimeoUrl(url) ||
    convertLoomUrl(url)
  );
}
